import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";

type Row = {
  key: string;
  appId: string;
  beneficiaryName: string;
  applicationId: string;
  templateId: string;
  templateTitle: string;
  phaseOpen: boolean;
  itemsDone: number;
  itemsTotal: number;
};

export function MilestonesPage() {
  const { applications, milestoneTemplates } = useAppState();
  const [show, setShow] = useState<"open" | "all">("open");

  const templateById = useMemo(
    () => new Map(milestoneTemplates.map((t) => [t.id, t])),
    [milestoneTemplates]
  );

  const rows = useMemo(() => {
    const list: Row[] = [];
    for (const a of applications) {
      for (const p of a.milestoneProgress) {
        const tpl = templateById.get(p.templateId);
        if (!tpl) continue;
        const itemsTotal = tpl.items.length;
        const itemsDone = tpl.items.filter((it) => {
          const ip = p.itemProgress.find((x) => x.templateItemId === it.id);
          return Boolean(ip?.completedAt);
        }).length;
        list.push({
          key: `${a.id}-${p.templateId}`,
          appId: a.id,
          beneficiaryName: a.mobile.beneficiaryName,
          applicationId: a.mobile.applicationId,
          templateId: p.templateId,
          templateTitle: tpl.title,
          phaseOpen: !p.milestoneCompletedAt,
          itemsDone,
          itemsTotal,
        });
      }
    }
    const filtered = show === "open" ? list.filter((r) => r.phaseOpen) : list;
    filtered.sort((a, b) => {
      if (a.phaseOpen !== b.phaseOpen) return a.phaseOpen ? -1 : 1;
      if (a.itemsDone / Math.max(1, a.itemsTotal) !== b.itemsDone / Math.max(1, b.itemsTotal)) {
        return a.itemsDone / Math.max(1, a.itemsTotal) - b.itemsDone / Math.max(1, b.itemsTotal);
      }
      return a.beneficiaryName.localeCompare(b.beneficiaryName);
    });
    return filtered;
  }, [applications, templateById, show]);

  const openPhaseCount = useMemo(
    () =>
      applications.reduce(
        (n, a) => n + a.milestoneProgress.filter((p) => !p.milestoneCompletedAt).length,
        0
      ),
    [applications]
  );

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Milestones" }]}
        title="Milestones"
        subtitle="Master milestone templates apply to every file. This table shows progress per beneficiary and phase."
      />

      <div className="help-banner help-banner--info" style={{ marginBottom: "1rem" }}>
        <strong>Master data</strong>
        <p style={{ margin: "0.35rem 0 0" }}>
          Edit phases and required items under <Link to="/milestone-master">Milestone master</Link>. Changes apply to
          all beneficiaries.
        </p>
      </div>

      <div className="toolbar-row" style={{ marginBottom: "1rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="field field-inline" style={{ marginBottom: 0 }}>
          <label htmlFor="ms-filter">Show</label>
          <select
            id="ms-filter"
            value={show}
            onChange={(e) => setShow(e.target.value as "open" | "all")}
          >
            <option value="open">Open phases only ({openPhaseCount})</option>
            <option value="all">All phases</option>
          </select>
        </div>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Across all applications</h2>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Beneficiary</th>
                <th>File ref.</th>
                <th>Milestone (from master)</th>
                <th>Items done</th>
                <th>Phase</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    {show === "open"
                      ? "No open phases. Switch to “All phases” or complete items under Milestones in a file."
                      : "No milestone data. Add templates under Milestone master."}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.key}>
                    <td>{r.beneficiaryName}</td>
                    <td className="mono">{r.applicationId}</td>
                    <td>
                      <strong>{r.templateTitle}</strong>
                    </td>
                    <td>
                      <span className="mono">
                        {r.itemsDone}/{r.itemsTotal}
                      </span>
                    </td>
                    <td>
                      {r.phaseOpen ? (
                        <span className="badge badge--pending">Open</span>
                      ) : (
                        <span className="badge badge--ok">Complete</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/applications/${r.appId}/milestones`} className="btn btn--primary btn--tg btn--sm">
                        Open in file
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
